import { useState, useCallback } from 'react';
import { unifiedClienteService, unifiedGrupoService } from '@/services/unifiedService';
import { toast } from 'sonner';

export interface ClienteData {
  id: number;
  nome: string;
  cpf: string;
  email: string;
  tel1: string;
  endereco?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  // Campos para PJ
  razaoSocial?: string;
  inscricaoEstadual?: string;
  nomeResponsavel?: string;
  cpfResponsavel?: string;
  emailResponsavel?: string;
  telResponsavel?: string;
}

export interface GrupoSugerido {
  id: number;
  nome: string;
  taxa_adm_total: number;
  motivo: string;
  score: number;
}

export interface BemSugerido {
  id: number;
  nome: string;
  codigo_bem: number;
  preco_bem: number;
  motivo: string;
  score: number;
}

export const useAutoPreenchimento = () => {
  const [loading, setLoading] = useState(false);
  const [dadosCliente, setDadosCliente] = useState<ClienteData | null>(null);
  const [gruposSugeridos, setGruposSugeridos] = useState<GrupoSugerido[]>([]);
  const [bensSugeridos, setBensSugeridos] = useState<BemSugerido[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  

  // Buscar cliente por CPF ou CNPJ
  const buscarPorCPF = useCallback(async (documento: string): Promise<ClienteData | null> => {
    if (!documento || documento.length < 11) {
      setDadosCliente(null);
      return null;
    }

    // Limpar documento (remover caracteres especiais)
    const documentoLimpo = documento.replace(/\D/g, '');
    
    // Validar se é CPF (11 dígitos) ou CNPJ (14 dígitos)
    if (documentoLimpo.length !== 11 && documentoLimpo.length !== 14) {
      setErro('Documento deve ter 11 dígitos (CPF) ou 14 dígitos (CNPJ)');
      return null;
    }

    // Validar documento
    if (documentoLimpo.length === 11 && !validarCPF(documentoLimpo)) {
      setErro('CPF inválido');
      return null;
    }
    
    if (documentoLimpo.length === 14 && !validarCNPJ(documentoLimpo)) {
      setErro('CNPJ inválido');
      return null;
    }

    setLoading(true);
    setErro(null);

    try {
      const response = await unifiedClienteService.findAll();
      const clientes = response.data || response || [];
      
      const cliente = clientes.find((c: any) => 
        c.cpf && c.cpf.replace(/\D/g, '') === documentoLimpo
      );

      if (cliente) {
        const dadosCliente: ClienteData = {
          id: cliente.id,
          nome: cliente.nome,
          cpf: cliente.cpf,
          email: cliente.email || '',
          tel1: cliente.tel1 || '',
          endereco: cliente.endereco,
          cidade: cliente.cidade,
          estado: cliente.estado,
          cep: cliente.cep,
          // Campos para PJ
          razaoSocial: cliente.razao_social || '',
          inscricaoEstadual: cliente.inscricao_estadual || '',
          nomeResponsavel: cliente.nome_responsavel || '',
          cpfResponsavel: cliente.cpf_responsavel || '',
          emailResponsavel: cliente.email_responsavel || '',
          telResponsavel: cliente.tel_responsavel || ''
        };

        setDadosCliente(dadosCliente);
        
        // Buscar sugestões baseadas no perfil do cliente
        await buscarSugestoes(dadosCliente);
        
        const tipoDocumento = documentoLimpo.length === 11 ? 'CPF' : 'CNPJ';
        toast.success(`Cliente encontrado: ${dadosCliente.nome} (${tipoDocumento})`);
        return dadosCliente;
      } else {
        setErro('Cliente não encontrado');
        toast.info('Cliente não encontrado. Você pode cadastrá-lo agora.');
        return null;
      }
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      setErro('Erro ao buscar cliente');
      toast.error('Erro ao buscar cliente');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Buscar sugestões de grupos e bens baseadas no perfil do cliente
  const buscarSugestoes = useCallback(async (cliente: ClienteData) => {
    try {
      // Buscar grupos
      const gruposResponse = await unifiedGrupoService.findAll();
      const grupos = gruposResponse.data || gruposResponse || [];
      
      // Sugerir grupos baseado no perfil do cliente
      const gruposSugeridos = sugerirGrupos(grupos, cliente);
      setGruposSugeridos(gruposSugeridos);

      // Buscar bens (se tiver grupo selecionado)
      if (gruposSugeridos.length > 0) {
        const bensSugeridos = sugerirBens(gruposSugeridos[0].id, cliente);
        setBensSugeridos(bensSugeridos);
      }
    } catch (error) {
      console.error('Erro ao buscar sugestões:', error);
    }
  }, []);

  // Sugerir grupos baseado no perfil do cliente
  const sugerirGrupos = useCallback((grupos: any[], cliente: ClienteData): GrupoSugerido[] => {
    return grupos
      .map(grupo => {
        let score = 0;
        let motivo = '';

        // Critérios de sugestão
        if (grupo.taxa_adm_total <= 5) {
          score += 30;
          motivo += 'Taxa administrativa baixa. ';
        }

        if (grupo.especie === 'Imóvel' && cliente.endereco) {
          score += 25;
          motivo += 'Cliente tem endereço cadastrado. ';
        }

        if (grupo.meses_restantes > 60) {
          score += 20;
          motivo += 'Grupo com boa duração restante. ';
        }

        if (grupo.maximo_participantes > 100) {
          score += 15;
          motivo += 'Grupo com boa liquidez. ';
        }

        // Critério baseado no último dígito do CPF
        const ultimoDigito = parseInt(cliente.cpf.slice(-1));
        if (ultimoDigito <= 4 && grupo.taxa_adm_total <= 5) {
          score += 10;
          motivo += 'Perfil ideal para taxa baixa. ';
        }

        return {
          id: grupo.id,
          nome: `Grupo ${grupo.numero_grupo} - ${grupo.especie}`,
          taxa_adm_total: grupo.taxa_adm_total,
          motivo: motivo.trim(),
          score
        };
      })
      .filter(grupo => grupo.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5); // Top 5 sugestões
  }, []);

  // Sugerir bens baseado no grupo e perfil do cliente
  const sugerirBens = useCallback((_grupoId: number, _cliente: ClienteData): BemSugerido[] => {
    // Esta função seria implementada quando tivermos acesso aos bens
    // Por enquanto, retorna array vazio
    return [];
  }, []);

  // Validar CPF
  const validarCPF = (cpf: string): boolean => {
    if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
      return false;
    }

    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let resto = 11 - (soma % 11);
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(9))) return false;

    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += parseInt(cpf.charAt(i)) * (11 - i);
    }
    resto = 11 - (soma % 11);
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cpf.charAt(10))) return false;

    return true;
  };

  // Validar CNPJ
  const validarCNPJ = (cnpj: string): boolean => {
    if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) {
      return false;
    }

    let soma = 0;
    let peso = 2;
    
    // Primeiro dígito verificador
    for (let i = 11; i >= 0; i--) {
      soma += parseInt(cnpj.charAt(i)) * peso;
      peso = peso === 9 ? 2 : peso + 1;
    }
    
    let resto = soma % 11;
    let digito1 = resto < 2 ? 0 : 11 - resto;
    
    if (digito1 !== parseInt(cnpj.charAt(12))) return false;

    // Segundo dígito verificador
    soma = 0;
    peso = 2;
    
    for (let i = 12; i >= 0; i--) {
      soma += parseInt(cnpj.charAt(i)) * peso;
      peso = peso === 9 ? 2 : peso + 1;
    }
    
    resto = soma % 11;
    let digito2 = resto < 2 ? 0 : 11 - resto;
    
    if (digito2 !== parseInt(cnpj.charAt(13))) return false;

    return true;
  };

  // Formatar CPF ou CNPJ
  const formatarCPF = (documento: string): string => {
    const documentoLimpo = documento.replace(/\D/g, '');
    
    // Se tem 14 dígitos ou mais, formata como CNPJ
    if (documentoLimpo.length >= 14) {
      const cnpjLimitado = documentoLimpo.substring(0, 14);
      
      if (cnpjLimitado.length <= 2) {
        return cnpjLimitado;
      } else if (cnpjLimitado.length <= 5) {
        return cnpjLimitado.replace(/(\d{2})(\d+)/, '$1.$2');
      } else if (cnpjLimitado.length <= 8) {
        return cnpjLimitado.replace(/(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
      } else if (cnpjLimitado.length <= 12) {
        return cnpjLimitado.replace(/(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
      } else {
        return cnpjLimitado.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d+)/, '$1.$2.$3/$4-$5');
      }
    } else {
      // Formata como CPF
      const cpfLimitado = documentoLimpo.substring(0, 11);
      
      if (cpfLimitado.length <= 3) {
        return cpfLimitado;
      } else if (cpfLimitado.length <= 6) {
        return cpfLimitado.replace(/(\d{3})(\d+)/, '$1.$2');
      } else if (cpfLimitado.length <= 9) {
        return cpfLimitado.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
      } else {
        return cpfLimitado.replace(/(\d{3})(\d{3})(\d{3})(\d+)/, '$1.$2.$3-$4');
      }
    }
  };

  // Limpar dados
  const limparDados = useCallback(() => {
    setDadosCliente(null);
    setGruposSugeridos([]);
    setBensSugeridos([]);
    setErro(null);
  }, []);

  return {
    // Estado
    loading,
    dadosCliente,
    gruposSugeridos,
    bensSugeridos,
    erro,
    
    // Funções
    buscarPorCPF,
    formatarCPF,
    validarCPF,
    limparDados,
    
    // Utilitários
    temCliente: !!dadosCliente,
    temSugestoes: gruposSugeridos.length > 0 || bensSugeridos.length > 0
  };
};